/*
 * Gift wrap: links gift bag and gift box lines to the bag they wrap and keeps them in step with it.
 * Loaded by layout/theme.liquid only when gift wrap linking is on (snippets/gift-wrap-options.liquid, "assets").
 *
 * A wrap line is a line of either wrap product carrying the hidden property _wrap_for (the bag's variant ID) and the
 * visible property For (the bag's title). On every cart change:
 * - a wrap's quantity follows the total quantity of its bag variant (Build your bag accessories, which carry
 *   _byb_role, do not count), and a wrap whose bag has gone is removed
 * - a bag with two wraps keeps the one just added, or else the newest, so choosing the other wrap replaces it
 * - the Gift note attribute is cleared once no wrap is left
 *
 * It uses the theme's cart flow: each request bundles the sections gathered by cart:prepare-bundled-sections and the
 * result goes out as cart:change, which re-renders the cart drawer and the cart count. The theme has no Ajax re-render
 * for the cart page, so this file bundles and swaps the main cart section itself. Nothing here reloads the page.
 */
(() => {
  const configElement = document.getElementById('gift-wrap-config');

  if (!configElement) {
    return;
  }

  const config = JSON.parse(configElement.textContent);
  const wrapProductIds = new Set(config.wrapProductIds.map(Number));
  const root = () => window.Shopify?.routes?.root || '/';
  const themeSettings = () => window.themeVariables?.settings || {};

  const isWrapLine = (item) => wrapProductIds.has(item.product_id) && Boolean(item.properties?._wrap_for);
  const countsAsBag = (item) => !wrapProductIds.has(item.product_id) && !item.properties?._byb_role;

  /* ------------------------------------------------------------------------------------------------------------------
   * Theme plumbing
   * ---------------------------------------------------------------------------------------------------------------- */

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    return sections.join(',');
  };

  // Resolves { ok, json } and never throws on an HTTP error; a network error rejects
  const post = async (endpoint, body, keepalive = false) => {
    const response = await fetch(`${root()}cart/${endpoint}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
      keepalive
    });

    let json = {};

    try {
      json = await response.json();
    } catch (error) {
      // An empty or non-JSON body is reported through ok
    }

    return { ok: response.ok, json };
  };

  const fetchCart = async () => (await fetch(`${root()}cart.js`)).json();

  const loading = (on) => {
    document.documentElement.dispatchEvent(new CustomEvent(on ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true }));
  };

  const refreshCart = () => document.dispatchEvent(new CustomEvent('cart:refresh'));

  // The cart drawer re-renders from cart.sections and throws if its own section is missing (it would be if the drawer
  // was not listening when the sections were bundled), so fall back to the theme's cart:refresh, which refetches it
  const drawerSectionMissing = (cart) => {
    const section = document.querySelector('cart-drawer')?.closest('.shopify-section');
    return Boolean(section) && !cart.sections?.[section.id.replace('shopify-section-', '')];
  };

  // baseEvent is never "variant:add" here, so the drawer re-renders without opening
  const announceCart = (cart, baseEvent) => {
    if (drawerSectionMissing(cart)) {
      refreshCart();
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart } }));
  };

  // The cart page's main cart section, marked by snippets/gift-wrap-cart-lines.liquid's table body
  const pageSectionId = () => {
    const section = document.querySelector('[data-gift-wrap-page]')?.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  };

  const replacePageSection = (id, html) => {
    const current = document.getElementById(`shopify-section-${id}`);
    const next = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);

    if (current && next) {
      current.innerHTML = next.innerHTML;
    }
  };

  document.addEventListener('cart:prepare-bundled-sections', (event) => {
    const id = pageSectionId();

    if (id && !event.detail.sections.includes(id)) {
      event.detail.sections.push(id);
    }
  });

  /* ------------------------------------------------------------------------------------------------------------------
   * Accessibility: a polite status for wraps added in the cart, and focus that survives the re-render
   * ---------------------------------------------------------------------------------------------------------------- */

  const status = document.createElement('p');
  status.className = 'sr-only';
  status.setAttribute('role', 'status');
  document.body.append(status);

  const say = (message) => {
    status.textContent = '';
    setTimeout(() => (status.textContent = message), 100);
  };

  const afterRender = () => new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 50)));

  // Only when the re-render took focus away (the control she used is gone), never when she has moved on
  const focusAfterRender = async (scope, selector, trigger) => {
    await afterRender();
    const active = document.activeElement;

    if (active && active !== document.body && active !== trigger && active.isConnected && !active.matches('cart-drawer')) {
      return;
    }

    const target = [...document.querySelectorAll(`${scope} ${selector}`)].find((element) => element.getClientRects().length > 0);
    target?.focus();
  };

  // Where a control lives, so focus lands in the same cart (drawer or page) after the re-render
  const scopeOf = (element) => (element.closest('cart-drawer') ? 'cart-drawer' : '[data-gift-wrap-page]');

  const showError = (container, message) => {
    const error = container?.querySelector('[data-gift-wrap-error]');

    if (error) {
      error.textContent = message;
    }
  };

  /* ------------------------------------------------------------------------------------------------------------------
   * Keeping wraps in step with their bags
   * ---------------------------------------------------------------------------------------------------------------- */

  let preferredKeys = [];
  let syncing = false;
  let syncAgain = false;

  // The changes the cart needs, or null
  const plan = (cart) => {
    const bagQuantities = new Map();

    cart.items.filter(countsAsBag).forEach((item) => {
      const bag = String(item.variant_id);
      bagQuantities.set(bag, (bagQuantities.get(bag) || 0) + item.quantity);
    });

    // A wrap just added from the product page wins; otherwise the newest line, which Shopify lists first
    const wraps = cart.items.filter(isWrapLine).sort((a, b) => preferredKeys.includes(b.key) - preferredKeys.includes(a.key));
    const kept = new Set();
    const updates = {};

    wraps.forEach((item) => {
      const bag = String(item.properties._wrap_for);
      const target = kept.has(bag) ? 0 : bagQuantities.get(bag) || 0;

      if (target > 0) {
        kept.add(bag);
      }

      if (item.quantity !== target) {
        updates[item.key] = target;
      }
    });

    const changes = {};

    if (Object.keys(updates).length > 0) {
      changes.updates = updates;
    }

    if (kept.size === 0 && cart.attributes?.['Gift note']) {
      changes.attributes = { 'Gift note': '' };
    }

    return Object.keys(changes).length > 0 ? changes : null;
  };

  const sync = async (cart) => {
    if (syncing) {
      syncAgain = true;
      return;
    }

    const changes = plan(cart);
    preferredKeys = [];

    if (!changes) {
      return;
    }

    syncing = true;
    loading(true);

    try {
      const result = await post('update', { ...changes, sections: bundledSections() });

      if (result.ok) {
        announceCart(result.json, 'gift-wrap:sync');
      }
    } catch (error) {
      // Left for the next cart change or page load to correct
    } finally {
      syncing = false;
      loading(false);
    }

    // A cart change arrived mid-request: its cart is stale now, so check the live one
    if (syncAgain) {
      syncAgain = false;
      fetchCart().then(sync).catch(() => {});
    }
  };

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail?.cart;

    if (!cart?.items) {
      return;
    }

    const id = pageSectionId();

    if (id && cart.sections?.[id]) {
      replacePageSection(id, cart.sections[id]);
    }

    if (event.detail.baseEvent !== 'gift-wrap:sync') {
      sync(cart);
    }
  });

  document.addEventListener('cart:refresh', () => fetchCart().then(sync).catch(() => {}));

  /* ------------------------------------------------------------------------------------------------------------------
   * Cart drawer and cart page: the offer row and the wrap's remove link
   * ---------------------------------------------------------------------------------------------------------------- */

  const addFromOffer = async (control) => {
    const offer = control.closest('[data-gift-wrap-offer]');

    if (!offer || offer.getAttribute('aria-busy') === 'true') {
      return;
    }

    const bag = offer.dataset.wrapFor;
    const label = (control.labels?.[0] || control).textContent.trim();
    const scope = scopeOf(offer);
    const controls = offer.querySelectorAll('[data-gift-wrap-add]');

    offer.setAttribute('aria-busy', 'true');
    controls.forEach((element) => (element.disabled = true));
    showError(offer, '');
    loading(true);

    let result;

    try {
      result = await post('add', {
        items: [{ id: Number(control.dataset.giftWrapAdd), quantity: Number(offer.dataset.quantity) || 1, properties: { For: offer.dataset.for, _wrap_for: bag } }],
        sections: bundledSections()
      });
    } catch (error) {
      result = { ok: false, json: {} };
    }

    loading(false);

    if (!result.ok) {
      offer.removeAttribute('aria-busy');
      controls.forEach((element) => (element.disabled = false));

      if (control.type === 'checkbox') {
        control.checked = false;
      }

      showError(offer, config.strings.error);
      return;
    }

    try {
      const cart = await fetchCart();
      cart.sections = result.json.sections;
      announceCart(cart, 'gift-wrap:add');
    } catch (error) {
      refreshCart();
    }

    say(`${label}. ${config.strings.added}`);
    focusAfterRender(scope, `[data-gift-wrap-for="${bag}"] [data-gift-wrap-remove]`, control);
  };

  const removeWrap = async (link) => {
    const bag = link.closest('[data-gift-wrap-for]')?.dataset.giftWrapFor;
    const scope = scopeOf(link);

    link.setAttribute('aria-disabled', 'true');
    loading(true);

    try {
      const result = await post('change', { id: link.dataset.giftWrapRemove, quantity: 0, sections: bundledSections() });

      if (result.ok) {
        announceCart(result.json, 'gift-wrap:remove');
        focusAfterRender(scope, `[data-gift-wrap-offer][data-wrap-for="${bag}"] [data-gift-wrap-add]`, link);
      } else {
        refreshCart();
      }
    } catch (error) {
      refreshCart();
    } finally {
      loading(false);
    }
  };

  document.addEventListener('click', (event) => {
    const add = event.target.closest?.('button[data-gift-wrap-add]');

    if (add) {
      addFromOffer(add);
      return;
    }

    const remove = event.target.closest?.('[data-gift-wrap-remove]');

    if (remove) {
      event.preventDefault();

      if (remove.getAttribute('aria-disabled') !== 'true') {
        removeWrap(remove);
      }
    }
  });

  /* ------------------------------------------------------------------------------------------------------------------
   * Gift note: saved to the Gift note cart attribute as she types. It is also a named field of the cart form, so
   * checking out saves it too
   * ---------------------------------------------------------------------------------------------------------------- */

  let noteTimer;
  let noteField = null;

  const saveNote = () => {
    clearTimeout(noteTimer);

    if (noteField) {
      post('update', { attributes: { 'Gift note': noteField.value.slice(0, 200) } }, true).catch(() => {});
      noteField = null;
    }
  };

  document.addEventListener('input', (event) => {
    const field = event.target.closest?.('[data-gift-wrap-note] textarea');

    if (field) {
      clearTimeout(noteTimer);
      noteField = field;
      noteTimer = setTimeout(saveNote, 600);
    }
  });

  document.addEventListener('focusout', (event) => {
    if (event.target === noteField) {
      saveNote();
    }
  });

  /* ------------------------------------------------------------------------------------------------------------------
   * Product page tick-box: one /cart/add.js request with the product and its wrap
   * ---------------------------------------------------------------------------------------------------------------- */

  const setPicker = (picker, on) => {
    const options = picker.querySelector('[data-gift-wrap-options]');

    if (options) {
      options.hidden = !on;
      options.querySelectorAll('input').forEach((input) => (input.disabled = !on));
    }

    // The express button would buy the product alone and lose the wrap
    picker.closest('form')?.querySelector('buy-buttons')?.classList.toggle('gift-wrap-hide-dynamic', on);
  };

  const resetPicker = (picker) => {
    const toggle = picker.querySelector('[data-gift-wrap-toggle]');
    const firstOption = picker.querySelector('[data-gift-wrap-options] input');

    toggle.checked = false;

    if (firstOption) {
      firstOption.checked = true;
    }

    setPicker(picker, false);
  };

  const restorePickers = () => {
    document.querySelectorAll('[data-gift-wrap-picker]').forEach((picker) => {
      setPicker(picker, picker.querySelector('[data-gift-wrap-toggle]')?.checked === true);
    });
  };

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (target.matches?.('[data-gift-wrap-toggle]')) {
      const picker = target.closest('[data-gift-wrap-picker]');
      showError(picker, '');
      setPicker(picker, target.checked);
    } else if (target.matches?.('input[type="checkbox"][data-gift-wrap-add]') && target.checked) {
      addFromOffer(target);
    }
  });

  const addWithWrap = async (form, picker, toggle) => {
    const data = new FormData(form);
    const product = { id: Number(data.get('id')), quantity: Number(data.get('quantity')) || 1, properties: {} };

    // The product's own properties (personalisation included) go through exactly as the form has them
    data.forEach((value, key) => {
      const property = key.match(/^properties\[(.+)\]$/);

      if (property && typeof value === 'string') {
        product.properties[property[1]] = value;
      }
    });

    if (data.get('selling_plan')) {
      product.selling_plan = Number(data.get('selling_plan'));
    }

    const wrapId = Number(toggle.dataset.variant || picker.querySelector('[data-gift-wrap-options] input:checked')?.value);
    const items = [product, { id: wrapId, quantity: product.quantity, properties: { For: picker.dataset.for, _wrap_for: String(product.id) } }];
    const submitButtons = Array.from(form.elements).filter((element) => element.type === 'submit');

    submitButtons.forEach((button) => {
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-busy', 'true');
    });

    showError(picker, '');
    loading(true);

    let result;

    try {
      result = await post('add', { items, sections: bundledSections(), sections_url: `${root()}variants/${product.id}` });
    } catch (error) {
      result = { ok: false, json: {} };
    }

    submitButtons.forEach((button) => {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
    });

    loading(false);

    if (!result.ok) {
      showError(picker, config.strings.error);

      // The theme's own error banner, with Shopify's reason when there is one
      if (result.json.description) {
        form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: result.json.description } }));
      }

      refreshCart();
      return;
    }

    resetPicker(picker);

    const settings = themeSettings();

    // As the theme's product form does when the cart is a page
    if (settings.cartType === 'page' || settings.pageType === 'cart') {
      window.location.href = `${root()}cart`;
      return;
    }

    const addedItems = result.json.items || [];
    preferredKeys = addedItems.filter((item) => wrapProductIds.has(item.product_id)).map((item) => item.key);

    let cart;

    try {
      cart = await fetchCart();
    } catch (error) {
      refreshCart();
      return;
    }

    cart.sections = result.json.sections;

    if (drawerSectionMissing(cart)) {
      refreshCart();
      return;
    }

    // The same events as the theme's product form, so the drawer opens and listeners see an ordinary add
    form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: addedItems, cart, onSuccessDo: data.get('on_success') } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: data.get('on_success'), cart } }));
  };

  // Capture phase: runs before the theme's product form handler, and only takes over when the tick-box is ticked
  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form') {
      return;
    }

    const picker = form.querySelector('[data-gift-wrap-picker]');
    const toggle = picker?.querySelector('[data-gift-wrap-toggle]');

    if (!toggle?.checked) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    addWithWrap(form, picker, toggle);
  }, true);

  /* ------------------------------------------------------------------------------------------------------------------
   * Start-up: correct the cart when the server-rendered cart flagged it, and restore tick-box state the browser kept
   * ---------------------------------------------------------------------------------------------------------------- */

  const init = async () => {
    restorePickers();

    if (!document.querySelector('[data-gift-wrap-sync]')) {
      return;
    }

    // The drawer must be listening before the sync bundles its section
    if (document.querySelector('cart-drawer')) {
      await customElements.whenDefined('cart-drawer');
    }

    fetchCart().then(sync).catch(() => {});
  };

  window.addEventListener('pageshow', (event) => {
    restorePickers();

    if (event.persisted) {
      fetchCart().then(sync).catch(() => {});
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
